import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  Chip,
  Box,
  Stack,
  CircularProgress,
  Tooltip,
  LinearProgress
} from '@mui/material';
import {
  Backup as BackupIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon,
  Restore as RestoreIcon,
  CloudUpload as UploadIcon,
  Refresh as RefreshIcon,
  Schedule as ScheduleIcon
} from '@mui/icons-material';
import { backupApi } from '../../api/backup.api';

const BackupManager = () => {
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [restoring, setRestoring] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, filename: '', action: '' });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [uploadProgress, setUploadProgress] = useState(0);

  const fetchBackups = async () => {
    setLoading(true);
    try {
      const response = await backupApi.listBackups();
      setBackups(response.data);
    } catch (error) {
      console.error('Error fetching backups:', error);
      showSnackbar(
        error.response?.data?.detail || 'Error al cargar backups',
        'error'
      );
    }
    setLoading(false);
  };

  const handleCreateBackup = async () => {
    setCreating(true);
    try {
      const response = await backupApi.createBackup();
      showSnackbar(`Backup creado: ${response.data.filename}`, 'success');
      fetchBackups();
    } catch (error) {
      showSnackbar(
        error.response?.data?.detail || 'Error al crear backup',
        'error'
      );
    }
    setCreating(false);
  };

  const handleDownloadBackup = async (filename) => {
    try {
      const response = await backupApi.downloadBackup(filename);

      // Crear link de descarga
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();

      showSnackbar(`Descargando: ${filename}`, 'success');
    } catch (error) {
      showSnackbar('Error al descargar backup', 'error');
    }
  };

  const handleDeleteBackup = async () => {
    const { filename } = confirmDialog;
    setDeleting(filename);
    try {
      await backupApi.deleteBackup(filename);
      showSnackbar(`Backup eliminado: ${filename}`, 'success');
      fetchBackups();
    } catch (error) {
      showSnackbar(
        error.response?.data?.detail || 'Error al eliminar backup',
        'error'
      );
    }
    setDeleting(null);
    setConfirmDialog({ open: false, filename: '', action: '' });
  };

  const handleRestoreBackup = async () => {
    const { filename } = confirmDialog;
    setRestoring(filename);
    try {
      const response = await backupApi.restoreBackup(filename);
      showSnackbar(response.data.message || 'Backup restaurado correctamente', 'success');
      fetchBackups();
    } catch (error) {
      showSnackbar(
        error.response?.data?.detail || 'Error al restaurar backup',
        'error'
      );
    }
    setRestoring(null);
    setConfirmDialog({ open: false, filename: '', action: '' });
  };

  const handleUploadBackup = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setUploading(true);
    setUploadProgress(0);

    try {
      const response = await backupApi.uploadBackup(file);
      showSnackbar(response.data.message || 'Backup subido correctamente', 'success');
      fetchBackups();
    } catch (error) {
      showSnackbar(
        error.response?.data?.detail || 'Error al subir backup',
        'error'
      );
    }
    setUploading(false);
    setUploadProgress(0);
    event.target.value = '';
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  useEffect(() => {
    fetchBackups();
    // Actualizar cada 60 segundos
    const interval = setInterval(fetchBackups, 60000);
    return () => clearInterval(interval);
  }, []);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('es-ES', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" component="h1">
          <BackupIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Gestión de Backups
        </Typography>
        <Stack direction="row" spacing={2}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchBackups}
            disabled={loading}
          >
            Actualizar
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<BackupIcon />}
            onClick={handleCreateBackup}
            disabled={creating}
          >
            {creating ? 'Creando...' : 'Crear Backup'}
          </Button>
          <Button
            variant="contained"
            color="secondary"
            component="label"
            startIcon={<UploadIcon />}
            disabled={uploading}
          >
            {uploading ? 'Subiendo...' : 'Subir Backup'}
            <input
              type="file"
              hidden
              accept=".zip,.sql"
              onChange={handleUploadBackup}
            />
          </Button>
        </Stack>
      </Box>

      {uploading && (
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="body2" gutterBottom>
              Subiendo backup...
            </Typography>
            <LinearProgress variant="determinate" value={uploadProgress} />
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              Backups Disponibles ({backups.length})
            </Typography>
            {loading && <CircularProgress size={24} />}
          </Box>

          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell>Nombre del Archivo</TableCell>
                  <TableCell>Tamaño</TableCell>
                  <TableCell>Fecha de Creación</TableCell>
                  <TableCell>Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {backups.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      <Typography color="textSecondary" sx={{ py: 2 }}>
                        No hay backups disponibles. Crea tu primer backup.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  backups.map((backup) => (
                    <TableRow key={backup.filename} hover>
                      <TableCell>
                        <Tooltip title={backup.path}>
                          <Typography variant="body2" noWrap sx={{ maxWidth: 300 }}>
                            {backup.filename}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Chip label={backup.size} size="small" color="info" />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {backup.created_at_display}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1}>
                          <Tooltip title="Descargar">
                            <IconButton
                              color="primary"
                              size="small"
                              onClick={() => handleDownloadBackup(backup.filename)}
                            >
                              <DownloadIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Restaurar">
                            <IconButton
                              color="warning"
                              size="small"
                              onClick={() => setConfirmDialog({
                                open: true,
                                filename: backup.filename,
                                action: 'restore'
                              })}
                              disabled={restoring === backup.filename}
                            >
                              <RestoreIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Eliminar">
                            <IconButton
                              color="error"
                              size="small"
                              onClick={() => setConfirmDialog({
                                open: true,
                                filename: backup.filename,
                                action: 'delete'
                              })}
                              disabled={deleting === backup.filename}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Diálogo de confirmación */}
      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false, filename: '', action: '' })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {confirmDialog.action === 'delete' ? 'Confirmar eliminación' : 'Confirmar restauración'}
        </DialogTitle>
        <DialogContent>
          {confirmDialog.action === 'delete' ? (
            <>
              <Typography>
                ¿Estás seguro de eliminar el backup <strong>{confirmDialog.filename}</strong>?
              </Typography>
              <Typography color="error" variant="body2" sx={{ mt: 2, p: 1, bgcolor: '#fff3e0', borderRadius: 1 }}>
                ⚠️ Esta acción no se puede deshacer.
              </Typography>
            </>
          ) : (
            <>
              <Typography>
                ¿Estás seguro de restaurar el backup <strong>{confirmDialog.filename}</strong>?
              </Typography>
              <Typography color="warning" variant="body2" sx={{ mt: 2, p: 1, bgcolor: '#fff8e1', borderRadius: 1 }}>
                ⚠️ Esta acción sobrescribirá los datos actuales.
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog({ open: false, filename: '', action: '' })}>
            Cancelar
          </Button>
          <Button
            onClick={confirmDialog.action === 'delete' ? handleDeleteBackup : handleRestoreBackup}
            color={confirmDialog.action === 'delete' ? 'error' : 'warning'}
            variant="contained"
            disabled={deleting === confirmDialog.filename || restoring === confirmDialog.filename}
          >
            {confirmDialog.action === 'delete' ? 'Eliminar' : 'Restaurar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar para notificaciones */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default BackupManager;
